import type { InjectionKey } from "vue";
import type { LmsProviderEnum } from "@simbelapp/order-sdk";
import { useActivitiesStore } from "~/store/activities/activities.store";
import { useCompaniesStore } from "~/store/companies/companies.store";
import { useCreateBuilderTrainingStore } from "~/store/trainings/create-training-builder.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { SessionItemFormatEnum } from "~/utils/enums/session-items.enums";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import type { IAddSessionTraining, IErrorObject, ITrainingSessions } from "~/utils/interfaces/trainings-interface";

/**
 * The « Ajouter une session » wizard, seen from one catalogue flow.
 *
 * The wizard is one set of components for the external training modal and the internal (builder)
 * one — QA asked for exactly that: « ce serait plus clean de ne pas maintenir les 2 flows entre
 * interne & externe ». Each store keeps its own shape, and this is where it is read as the wizard
 * needs it, so neither the wizard nor either store has to know about the other side.
 *
 * Every field is a getter rather than a `computed`: read straight from a template, it tracks the
 * store the same way, and it needs no `.value` at the call site.
 */
export interface IAddSessionFlow {
  /** The store slice the wizard edits in place: the draft, and its step dialogs' flags. */
  readonly sessions: ITrainingSessions;
  /** Validation messages keyed by property path, as `pushValidationErrors` writes them. */
  readonly errors: IErrorObject;
  /** Everything else the wizard reads off the training. */
  readonly training: IAddSessionTraining;
  /** The LMS the company syncs with: Docebo caps the sessions the wizard creates. */
  readonly lmsProvider: LmsProviderEnum | null;
  /** Runs the session-step rules; the wizard reads the outcome back from `errors`. */
  validate: () => Promise<void>;
  /** Creates the session, and closes the wizard once it is saved. */
  submit: () => Promise<void>;
  /** Drops the draft and closes the wizard. */
  close: () => void;
}

const ADD_SESSION_FLOW: InjectionKey<IAddSessionFlow> = Symbol("addSessionFlow");

/** Publishes the flow for the wizard mounted under this Sessions tab. */
export function provideAddSessionFlow(flow: IAddSessionFlow): void {
  provide(ADD_SESSION_FLOW, flow);
}

/**
 * The flow the enclosing Sessions tab published — the wizard's only way to its store, so that one
 * component serves the external modal and the internal one without knowing which it is in.
 */
export function useAddSessionFlow(): IAddSessionFlow {
  const flow = inject(ADD_SESSION_FLOW);
  if (!flow) throw new Error("The add-session wizard needs its Sessions tab to provide a flow");

  return flow;
}

/** The external training modal, which also hosts internal trainings given in class. */
export function useCatalogAddSessionFlow(): IAddSessionFlow {
  const createTrainingStore = useCreateTrainingStore();
  const companiesStore = useCompaniesStore();

  return {
    get sessions() {
      return createTrainingStore.training.sessions;
    },
    get errors() {
      return createTrainingStore.training.errors;
    },
    get training() {
      const { general, sessionItems } = createTrainingStore.training;

      return {
        type: general.type,
        format: general.format,
        durationHours: general.durationHours ?? 0,
        individual: sessionItems.individual,
        // Intra sessions only exist on an external training: on an internal one the collective
        // card would have no session item behind it.
        group: general.type === TrainingTypeEnum.EXTERNAL ? sessionItems.group : null,
      };
    },
    get lmsProvider() {
      return companiesStore.lmsProvider?.lmsProvider ?? null;
    },
    validate: () => createTrainingStore.validation(),
    submit: () => createTrainingStore.addSession(),
    close: () => createTrainingStore.openAddSession(),
  };
}

/** The internal builder modal, whose trainings are eLearning and have a single inscription type. */
export function useBuilderAddSessionFlow(): IAddSessionFlow {
  const createBuilderTrainingStore = useCreateBuilderTrainingStore();
  const activitiesStore = useActivitiesStore();
  const companiesStore = useCompaniesStore();

  return {
    get sessions() {
      return createBuilderTrainingStore.training.sessions;
    },
    get errors() {
      return createBuilderTrainingStore.training.errors;
    },
    get training() {
      return {
        type: TrainingTypeEnum.INTERNAL_BUILDER,
        // A training built here is made of online activities, so its sessions are eLearning.
        format: SessionItemFormatEnum.ONLINE,
        durationHours: totalActivitiesHours(activitiesStore.activities),
        // The builder has the one inscription type, and it is never opt-out.
        individual: { ...createBuilderTrainingStore.training.sessionItems.individual, isChecked: true },
        group: null,
      };
    },
    get lmsProvider() {
      return companiesStore.lmsProvider?.lmsProvider ?? null;
    },
    validate: () => createBuilderTrainingStore.validation(),
    submit: () => createBuilderTrainingStore.addSession(),
    close: () => createBuilderTrainingStore.openAddSession(),
  };
}

/**
 * A built training carries no duration of its own: it is whatever its activities add up to, which
 * is the figure the step form spreads over the steps and warns against.
 */
export function totalActivitiesHours(activities: { durationMinutes?: number | null }[]): number {
  const minutes = activities.reduce((total, activity) => total + Number(activity.durationMinutes ?? 0), 0);

  return Math.round((minutes / 60) * 10) / 10;
}
